import {
  Button,
  Heading,
  Modal,
  SectionHeader,
  Spinner,
  Tag,
  Text,
} from "@ttc3k/trekker";
import {
  BroadcastByUserQuery,
  BroadcastManyQuery,
  EnumBroadcastResourcesType,
  EnumOwnerType,
  EnumUserUserType,
  BroadcastsByIdsStatsForSingleUserQuery,
  useOrganizationNameByIdQuery,
  useBroadcastTeaserByIdQuery,
} from "gql/generated";
import { useTranslation } from "react-i18next";
import { Center, Flex } from "styled-system/jsx";
import {
  CustomResourceIframeModal,
  HeaderImage,
  ResourceCard,
  ViewResource,
} from "./components";
import { getMultilingualString } from "shared/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ViewResourceModal } from "./ViewResourceModal";
import { useAppContext } from "useAppContext";
import { BroadcastError } from "./components/BroadcastError";
import { BroadcastTeaser } from "./components/BroadcastTeaser";
import { getBroadcastInError } from "./utils";
import { useBroadcastPostHogEvents } from "./hooks/useBroadcastPostHogEvents";
import { useFormResourceStatuses } from "./hooks/useFormResourceStatuses";
import { BroadcastStatusIndicator } from "shared/components";
import { getResourceStatus } from "apps/home/constants";
import { BroadcastModalLayout } from "./BroadcastModalLayout";
import { useCurrentUserBookerPrefill } from "widgets/booker/shared/hooks/useCurrentUserBookerPrefill";
import {
  MembershipCheckoutIntentProvider,
  type MembershipCheckoutIntentValue,
} from "shared/contextProviders/membershipCheckoutIntent";

type ViewBroadcastModalProps = {
  isOpen: boolean;
  onClose: () => void;
  broadcast:
    | (
        | BroadcastManyQuery["broadcastMany"][0]
        | BroadcastByUserQuery["broadcastByUser"][0]
      )
    | null;
  /** The broadcast ID from the URL, used to fetch teaser content when broadcast is null. */
  broadcastId?: string | null;
  /** When set, open the resource flyout for this resource id once the broadcast is loaded (e.g. from a lead "Source: Broadcast" link). */
  initialResourceId?: string | null;
  /** Called when the active resource changes (opened, navigated, or closed). Receives null when no resource is active. */
  onResourceChange?: (resourceId: string | null) => void;
  isPreview: boolean;
  broadcastStatsData?: BroadcastsByIdsStatsForSingleUserQuery["broadcastsByIdsStatsForSingleUser"][0];
  displayDeletedErrorInPreview?: boolean;
  /** Renew / join / change-tier context for the membership checkout inside this broadcast. */
  membershipCheckoutIntent?: MembershipCheckoutIntentValue | null;
};

export const ViewBroadcastModal = ({
  isOpen,
  onClose,
  broadcast,
  broadcastId,
  initialResourceId,
  onResourceChange,
  isPreview,
  broadcastStatsData,
  displayDeletedErrorInPreview = false,
  membershipCheckoutIntent = null,
}: ViewBroadcastModalProps) => {
  const { t } = useTranslation("home", {
    keyPrefix: "VIEWS.BROADCAST",
  });

  const { dbPerson, dbUser } = useAppContext();

  const { data: orgNameData, loading: orgNameLoading } =
    useOrganizationNameByIdQuery({
      variables: { id: broadcast?.owner.entityId },
      skip:
        !broadcast?.owner.entityId ||
        broadcast.owner.type !== EnumOwnerType.Organization,
    });

  const { data: teaserData } = useBroadcastTeaserByIdQuery({
    variables: { broadcastId: broadcastId! },
    skip: !!broadcast || !broadcastId,
  });

  const personId = dbPerson?._id ?? undefined;
  const personName =
    dbPerson?.firstName && dbPerson?.lastName
      ? `${dbPerson.firstName} ${dbPerson.lastName}`
      : undefined;

  const { defaultContact, userOperators } = useCurrentUserBookerPrefill();

  const isAdmin =
    dbUser?.userType === EnumUserUserType.TnbInternal ||
    (dbUser?.access?.organizations?.length ?? 0) > 0;

  const { trackBroadcastViewedEvent, trackResourceViewedEvent } =
    useBroadcastPostHogEvents(
      personId,
      broadcast?._id,
      personName,
      broadcast?.owner ?? undefined,
      broadcast?.name ?? undefined,
    );

  const stableResources = useMemo(
    () =>
      (broadcast?.resources?.filter(Boolean) ?? []) as NonNullable<
        BroadcastManyQuery["broadcastMany"][0]["resources"][0]
      >[],
    [broadcast?.resources],
  );

  const { formStatusMap } = useFormResourceStatuses(
    stableResources,
    broadcast?._id,
    personId,
    isPreview,
  );

  // Dirty state tracking for single-resource form
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);

  const handleDirtyChange = useCallback((dirty: boolean) => {
    setIsDirty(dirty);
  }, []);

  const guardAction = useCallback(
    (action: () => void) => {
      if (isDirty) {
        pendingActionRef.current = action;
        setShowUnsavedDialog(true);
      } else {
        action();
      }
    },
    [isDirty],
  );

  const handleGuardedClose = useCallback(() => {
    guardAction(onClose);
  }, [guardAction, onClose]);

  const handleLeave = useCallback(() => {
    setShowUnsavedDialog(false);
    setIsDirty(false);
    pendingActionRef.current?.();
    pendingActionRef.current = null;
  }, []);

  const handleCancelDialog = useCallback(() => {
    setShowUnsavedDialog(false);
    pendingActionRef.current = null;
  }, []);

  // Broadcast error states
  const {
    isInError,
    isBroadcastDeleted,
    isBroadcastUnpublished,
    displayError,
  } = getBroadcastInError(isPreview, displayDeletedErrorInPreview, broadcast);

  useEffect(() => {
    // Track the broadcast viewed event
    // Only track the event if the modal is opened, the broadcast exists,
    // the modal is not in preview mode, the modal is not in error
    // and the people query has been loaded
    if (isOpen && !!broadcast && !isPreview && !isInError && dbPerson) {
      trackBroadcastViewedEvent();
    }
  }, [
    broadcast,
    isOpen,
    isPreview,
    dbPerson,
    trackBroadcastViewedEvent,
    isInError,
  ]);

  const [isResourceModalOpen, setIsResourceModalOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState<NonNullable<
    BroadcastManyQuery["broadcastMany"][0]["resources"][0]
  > | null>(null);
  const [customIframeResource, setCustomIframeResource] = useState<NonNullable<
    BroadcastManyQuery["broadcastMany"][0]["resources"][0]
  > | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setCustomIframeResource(null);
      setIsResourceModalOpen(false);
      setSelectedResource(null);
      setShowUnsavedDialog(false);
      pendingActionRef.current = null;
    }
  }, [isOpen]);

  // When opening custom from the resource list teaser, open the iframe directly (skip the intermediate resource flyout)
  useEffect(() => {
    if (!customIframeResource || isPreview || !isOpen) {
      return;
    }
    trackResourceViewedEvent(
      customIframeResource._id,
      customIframeResource.title,
      EnumBroadcastResourcesType.Custom,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- track once per opened resource id; object identity may change on refetch
  }, [customIframeResource?._id, isPreview, isOpen, trackResourceViewedEvent]);

  // When opening with initialResourceId (e.g. from lead "Source: Broadcast"), open that resource's flyout
  useEffect(() => {
    if (
      isOpen &&
      broadcast &&
      initialResourceId &&
      broadcast.resources?.length
    ) {
      const resource = broadcast.resources.find(
        r => r && r._id === initialResourceId,
      );
      if (resource) {
        if (
          resource.type === EnumBroadcastResourcesType.Custom &&
          resource.iframeUrl
        ) {
          setCustomIframeResource(resource);
        } else {
          setSelectedResource(resource);
          setIsResourceModalOpen(true);
        }
      }
    }
  }, [isOpen, broadcast, initialResourceId]);

  const organizationName = useMemo(
    () =>
      broadcast?.owner.type === EnumOwnerType.Organization
        ? orgNameData?.organizationById?.name
        : broadcast?.owner.type === EnumOwnerType.TnbInternal
          ? { en: "Whereabouts", es: "Whereabouts", fr: "Whereabouts" }
          : undefined,
    [broadcast, orgNameData],
  );

  const getEffectiveStatus = useCallback(
    (resource: NonNullable<BroadcastManyQuery["broadcastMany"][0]["resources"][0]>) => {
      if (
        (resource.type === EnumBroadcastResourcesType.Forms ||
          resource.type === EnumBroadcastResourcesType.Storefront) &&
        formStatusMap[resource._id]
      ) {
        return formStatusMap[resource._id].status;
      }
      return getResourceStatus(resource._id, broadcastStatsData);
    },
    [formStatusMap, broadcastStatsData],
  );

  // If no broadcast is found, we can assume that the broadcast has not been
  // shared with the user & therefore we will display that error
  // (or a teaser message if one is configured)
  if (!broadcast) {
    const teaserDescription =
      teaserData?.broadcastTeaserById?.description ?? null;

    return (
      <Modal
        open={isOpen}
        onOpenChange={({ open }: { open: boolean }) => {
          if (!open) onClose();
        }}
        hideCloseTrigger
        portal
      >
        <Flex
          flexDir={"column"}
          px={"500"}
          pb={"500"}
          bg={"bg.wa.light"}
          width={"full"}
          minH={"100vh"}
          height={"auto"}
          position={"relative"}
        >
          {teaserDescription ? (
            <BroadcastTeaser
              description={teaserDescription}
              onCloseModal={onClose}
            />
          ) : (
            <BroadcastError
              errorType={"NOT_SHARED_WITH_ME"}
              ownerName={organizationName}
              onCloseModal={onClose}
            />
          )}
        </Flex>
      </Modal>
    );
  }

  const isNoResources = broadcast.resources.length === 0;
  const isSingleResource = broadcast.resources.length === 1;

  return (
    <MembershipCheckoutIntentProvider value={membershipCheckoutIntent}>
      <BroadcastModalLayout
        isOpen={isOpen}
        onClose={handleGuardedClose}
        isPreview={isPreview}
      >
        {!dbPerson || orgNameLoading ? (
          <Center width={"full"}>
            <Spinner w={"38px"} h={"38px"} />
          </Center>
        ) : (
          <>
            {displayError ? (
              // If the broadcast is not in a Preview state and has been deleted, unpublished, or not shared with me, display the appropriate error screen
              <BroadcastError
                errorType={
                  isBroadcastDeleted
                    ? "DELETED"
                    : isBroadcastUnpublished
                      ? "UNPUBLISHED"
                      : "NOT_SHARED_WITH_ME"
                }
                ownerName={organizationName}
                onCloseModal={onClose}
              />
            ) : (
              <>
                {isSingleResource && broadcast.resources[0] ? (
                  <ViewResource
                    resource={broadcast.resources[0]}
                    organizationName={organizationName}
                    personId={personId}
                    isPreview={isPreview}
                    broadcastId={broadcast._id}
                    status={getEffectiveStatus(broadcast.resources[0])}
                    submittedCount={formStatusMap[broadcast.resources[0]._id]?.submittedCount}
                    pendingCount={formStatusMap[broadcast.resources[0]._id]?.pendingCount}
                    draftCount={formStatusMap[broadcast.resources[0]._id]?.draftCount}
                    rejectedCount={formStatusMap[broadcast.resources[0]._id]?.rejectedCount}
                    personName={personName}
                    broadcastOwner={broadcast?.owner}
                    broadcastName={broadcast.name}
                    onDirtyChange={handleDirtyChange}
                    defaultContact={defaultContact}
                    userOperators={userOperators}
                    isAdmin={isAdmin}
                  />
                ) : (
                  <Flex flexDir={"column"} gap={"400"}>
                    {/* Broadcast data */}
                    <Flex flexDir={"column"} gap={"300"}>
                      <Flex flexDir={"column"} gap={"150"}>
                        <Flex flexDir={"column"} gap={"50"}>
                          <Text visual={"smallTag"} color={"text.light"}>
                            {getMultilingualString(organizationName ?? {})}
                          </Text>
                          <Heading size={"h4"} color={"text.dark"}>
                            {broadcast.publicName &&
                            getMultilingualString(broadcast.publicName).length >
                              0
                              ? getMultilingualString(broadcast.publicName)
                              : getMultilingualString(broadcast.name)}
                          </Heading>
                        </Flex>
                        <Flex alignItems={"center"} gap={"100"}>
                          <Tag mode={"light"}>
                            {t("RESOURCE_TYPE.COLLECTION")}
                          </Tag>
                          <BroadcastStatusIndicator
                            status={
                              broadcastStatsData?.viewed
                                ? "VIEWED"
                                : "NOT_VIEWED"
                            }
                          />
                        </Flex>
                      </Flex>

                      {!isNoResources && (
                        <HeaderImage
                          type={"DEFAULT"}
                          image={broadcast.coverImage}
                          doNotShowFallback
                        />
                      )}

                      {broadcast.description && (
                        <Text visual={"text.mid"}>
                          {getMultilingualString(broadcast.description)}
                        </Text>
                      )}
                    </Flex>

                    {/* Resources */}
                    {broadcast.resources.length > 0 && (
                      <Flex flexDir={"column"} gap={"250"}>
                        <SectionHeader
                          text={t("VIEW_MODAL.RESOURCES")}
                          divider
                        />
                        {broadcast.resources.map(r => {
                          if (!r) return null;
                          return (
                            <ResourceCard
                              key={r._id}
                              resource={r}
                              onResourceClick={resource => {
                                if (
                                  resource.type ===
                                    EnumBroadcastResourcesType.Custom &&
                                  resource.iframeUrl
                                ) {
                                  setCustomIframeResource(resource);
                                  onResourceChange?.(resource._id);
                                } else {
                                  setSelectedResource(resource);
                                  setIsResourceModalOpen(true);
                                  onResourceChange?.(resource._id);
                                }
                              }}
                              status={getEffectiveStatus(r)}
                              allowSaveAndEdit={r.allowSaveAndEdit ?? false}
                              formProgress={
                                formStatusMap[r._id]?.formProgress ?? null
                              }
                              submittedCount={formStatusMap[r._id]?.submittedCount}
                              pendingCount={formStatusMap[r._id]?.pendingCount}
                              draftCount={formStatusMap[r._id]?.draftCount}
                              rejectedCount={formStatusMap[r._id]?.rejectedCount}
                              bookedCount={formStatusMap[r._id]?.bookedCount}
                            />
                          );
                        })}
                      </Flex>
                    )}
                  </Flex>
                )}
              </>
            )}
          </>
        )}
      </BroadcastModalLayout>
      {customIframeResource?.iframeUrl && (
        <CustomResourceIframeModal
          open={!!customIframeResource}
          onClose={() => {
            setCustomIframeResource(null);
            onResourceChange?.(null);
          }}
          iframeUrl={customIframeResource.iframeUrl}
        />
      )}

      <ViewResourceModal
        isOpen={isResourceModalOpen}
        onClose={() => {
          setIsResourceModalOpen(false);
          setSelectedResource(null);
          onResourceChange?.(null);
        }}
        isPreview={isPreview}
        resource={selectedResource}
        organizationName={organizationName}
        personId={personId}
        broadcastId={broadcast._id}
        broadcastOwner={broadcast?.owner}
        personName={personName}
        status={selectedResource ? getEffectiveStatus(selectedResource) : "NOT_VIEWED"}
        formStatusMap={formStatusMap}
        broadcastName={broadcast.name}
        allResources={broadcast.resources}
        onNavigate={resource => {
          setSelectedResource(resource);
          onResourceChange?.(resource._id);
        }}
        defaultContact={defaultContact}
        userOperators={userOperators}
        isAdmin={isAdmin}
      />

      {/* Unsaved changes confirmation dialog */}
      <Modal
        open={showUnsavedDialog}
        onOpenChange={() => setShowUnsavedDialog(false)}
        placement="centerPane"
        portal
      >
        <Flex
          flexDir="column"
          gap="300"
          p="400"
          bg="white"
          borderRadius="200"
          maxW="400px"
          mx="auto"
        >
          <Text visual="bodySemiBold" color="text.dark">
            {t("FORM_UNSAVED_TITLE")}
          </Text>
          <Text visual="bodyRegular" color="text.mid">
            {t("FORM_UNSAVED_MESSAGE")}
          </Text>
          <Flex gap="150" justifyContent="flex-end">
            <Button
              mode="light"
              onClick={handleCancelDialog}
              type="button"
              size="sm"
            >
              {t("FORM_UNSAVED_CANCEL")}
            </Button>
            <Button
              visual="destructive"
              onClick={handleLeave}
              type="button"
              size="sm"
            >
              {t("FORM_UNSAVED_LEAVE")}
            </Button>
          </Flex>
        </Flex>
      </Modal>
    </MembershipCheckoutIntentProvider>
  );
};
